const cloneDeep = require('lodash/cloneDeep');
// eslint-disable-next-line no-unused-vars
const jsdocTypes = require('../../../../jsdoc-types');
const { BODY_EVENTS } = require('../analytics/analyticsConst');
const { isString } = require('../common/utils/type');
const { getPidFromQueryString } = require('../common/utils/queryString');

BODY_EVENTS.customerLogin = 'customer:login';

/**
 * Get the current customer ID from the gaInfo object
 * @returns {string} current customer ID
 */
const getCurrentCustomerID = () => gaInfo?.customerID ?? '';

/**
 * Generate key for cart in local storage
 * @param {string} [customerID] The ID of the customer
 * @returns {string} Cart-${customerID}-${shopByL} (ex. Cart-bcASL7xo6HWtz2XDOo9LI1sxaO-SG)
 */
const cartKey = (customerID = getCurrentCustomerID()) =>
  `Cart-${customerID}-${gaInfo?.shopByL}`;

/**
 * Compare given eventType with cart related events
 * product:afterAddToCart, product:removeFromCart, product:updateQuantity
 * @param {string} eventType type of current event
 * @returns {boolean}
 */
const isEventAddToCart = (eventType) => eventType === BODY_EVENTS.addedToCart;
const isEventRemoveFromCart = (eventType) =>
  eventType === BODY_EVENTS.removeFromCart;
const isEventUpdateQuantity = (eventType) =>
  eventType === BODY_EVENTS.updateQuantity;

/**
 * Clone cart content from different data structures
 * @param {Object|jsdocTypes.Cart} data current event data
 * @param {jsdocTypes.Cart} [data.basket] cart data from removing
 * @param {jsdocTypes.Cart} [data.cart] cart data from adding
 * @param {boolean} key indicates if cart info is inside a key (cart|basket) of data
 * @returns {jsdocTypes.Cart} deepCloned cart
 */
const getCartFromData = (data, key) => {
  const dataToClone = key ? data.cart || data.basket : data;
  const cart = cloneDeep(dataToClone);
  return cart;
};

/**
 * Get cart stored in localStorage for current customer
 * @returns {jsdocTypes.Cart|null} if cart doesn't exists in localStorage, return null
 */
const getCartFromLocalStorage = () => {
  const cart = localStorage.getItem(cartKey()) ?? null;
  return JSON.parse(cart);
};

/**
 * Remove the cart of current user from localStorage
 */
const removeCartFromLocalStorage = () => {
  localStorage.removeItem(cartKey());
};

/**
 * Add cart info to localStorage
 * @param {jsdocTypes.Cart|string} cart current cart info
 * @param {string} destinationCustomerID the ID of the customer associated to
 * the cart
 */
const setCartToCustomerInLocalStorage = (cart, destinationCustomerID) => {
  const cartAsString = isString(cart) ? cart : JSON.stringify(cart);
  localStorage.setItem(cartKey(destinationCustomerID), cartAsString);
};

/**
 * Filter cart items
 * @param {jsdocTypes.ProductLineItem[]} cartItems current cart items
 * @param {jsdocTypes.ProductLineItem[]} cartFromLocalStorageItems stored cart items
 * @param {string} productId product id to filter cart.items
 * @returns {jsdocTypes.ProductLineItem[]} cart.items excluding item with given productId
 */
const getItemsFiltered = (cartItems, cartFromLocalStorageItems, productId) => {
  const cartItemsPids = cartItems.map((item) => item.id);
  return cartFromLocalStorageItems.filter(
    (storedItem) =>
      cartItemsPids.includes(storedItem.id) && storedItem.id !== productId
  );
};

/**
 * Find a product in given cart
 * @param {string} productId product id to be found
 * @param {jsdocTypes.Cart} [cart] target cart
 * @returns {jsdocTypes.ProductLineItem|null} matched cart item with given productId
 */
function findProductInCart(productId, cart = getCartFromLocalStorage()) {
  return cart?.items.find((item) => item.id === productId) ?? null;
}

/**
 * Filter current cart items excluding given product.id
 * If a product is being added or updated, push product to array
 * @param {string} eventType type of user event
 * @param {jsdocTypes.ProductLineItem[]} cartItems current cart items
 * @param {jsdocTypes.ProductLineItem[]} cartFromLocalStorageItems stored cart items
 * @param {jsdocTypes.ProductLineItem} product current added|removed|updated product
 * @returns {jsdocTypes.ProductLineItem[]} new array of cart items
 */
const buildItemsArray = (
  eventType,
  cartItems,
  cartFromLocalStorageItems,
  product
) => {
  const items = getItemsFiltered(
    cartItems,
    cartFromLocalStorageItems,
    product.id
  );
  if (product && !isEventRemoveFromCart(eventType)) {
    items.push(product);
  }
  return items;
};

/**
 * Initialize localStorage cart data
 * @param {Object} data current event data, including cart info
 * @param {Object} event config object
 * @param {boolean} config.key indicates if cart info is inside a key (cart|basket) of data
 * @param {boolean} config.getProductFromData indicates if product has to be extracted from data (added or updated product)
 * @param {boolean} config.getDoubleLookPIDFromData indicates if product doubleLookPID has to be updated
 * @returns {Object} data - initial data for localStorage cart
 * @returns {Object} data.cart - cart received from data, it can be the data itself or inside a key in data (basket | cart)
 * @returns {Object} data.cartFromLocalStorage - cart info stored in client localStorage
 * @returns {Object|undefined} data.product - product received in data, it is undefined in removeFromCart event
 */
const initData = (
  data,
  { key = false, getProductFromData = false, getDoubleLookPIDFromData = false }
) => {
  const cart = getCartFromData(data, key);
  const productId = data.product
    ? data.product.id
    : data.pid || getPidFromQueryString(data.queryString);
  const cartFromLocalStorage = getCartFromLocalStorage();
  const cartToGetProduct = getProductFromData ? cart : cartFromLocalStorage;
  let product;
  if (cartToGetProduct) {
    product = findProductInCart(productId, cartToGetProduct);
    if (product && getDoubleLookPIDFromData) {
      product.doubleLookPID = data.doubleLookPID;
    }
  }
  return {
    cart,
    cartFromLocalStorage,
    product,
  };
};

/**
 * Calculate the proper url of the product
 * @param {Object} product current product
 * @returns {string} url formed url product
 */
const getProductUrl = (product) => {
  const urlProductWithNoVariation = product.selectedProductUrl?.split('?')[0];
  const splittedUrl = urlProductWithNoVariation.split('.');
  const matchColor = product.id.match(
    /\.([a-zA-Z][0-9]{4}|\d{1}\w{1}\d{3})(?:_DL\d)?/
  );
  const matches = matchColor && matchColor.length > 1 && matchColor[1];
  const colorVariationId = matches;
  return `${window.location.origin}${splittedUrl[0]}.${colorVariationId}.${splittedUrl[1]}`;
};

module.exports = {
  init() {
    // Init cart in localStorage on product:afterAddToCart, product:removeFromCart, product:updateQuantity
    $('body').on(
      [
        BODY_EVENTS.addedToCart,
        BODY_EVENTS.removeFromCart,
        BODY_EVENTS.updateQuantity,
      ].join(' '),
      /**
       * Handle cart modification
       * @param {jQuery.Event} event The event
       * @param {Object} data Data (mainly from the server controller)
       *
       * @param {Cart} [data.basket] cartModel from removeFromCart
       * @param {Cart} [data.cart] cartModel from afterAddToCart
       *
       * @param {Object} [data.newBonusDiscountLineItem] new bonus discount line item
       * @param {string} [data.minicartCountOfItems] Formatted msg
       * @param {string} [data.pliUUID] The uuid of the product line item
       * @param {number} [data.quantityTotal] The total quantity
       * @param {string} [data.reportingURL] URL
       * @param {string} [data.uuid] Unknown purpose
       *
       * @param {boolean} [data.error] true if an error ocurred
       * @param {string} [data.message] The error message
       * @returns {void}
       */
      (event, data) => {
        if (data.error) return;
        // In product:afterAddToCart and product:removeFromCart, cart info is inside a key in data (cart|basket)
        const key =
          isEventAddToCart(event.type) || isEventRemoveFromCart(event.type);
        // In product:afterAddToCart and product:updateQuantity, we have to get the product from data
        const getProductFromData =
          isEventAddToCart(event.type) || isEventUpdateQuantity(event.type);
        const getDoubleLookPIDFromData = isEventAddToCart(event.type);

        const { cart, cartFromLocalStorage, product } = initData(data, {
          key,
          getProductFromData,
          getDoubleLookPIDFromData,
        });

        if (product) {
          // Handle product doubleLookPID
          if (!getDoubleLookPIDFromData && !product.doubleLookPID) {
            product.doubleLookPID = findProductInCart(
              product.id,
              cartFromLocalStorage
            )?.doubleLookPID;
          }
          // Handle product url
          if (data.product) {
            product.productUrl = getProductUrl(data.product);
          }
          // Add TimeStamp
          if (Date.now()) {
            const time = Date.now();
            const today = new Date(time);
            cart.timestampDate = today.toUTCString();
            cart.timestamp = time;
          }
          // Handle cart
          if (cartFromLocalStorage) {
            cart.items = buildItemsArray(
              event.type,
              cart.items,
              cartFromLocalStorage.items,
              product
            );
          }
          if (cart.items.length > 0) {
            setCartToCustomerInLocalStorage(cart, getCurrentCustomerID());
          } else {
            removeCartFromLocalStorage();
          }
        }
      }
    );

    // Update cart key in localStorage when user is authenticated
    // data = registered customerID
    $('body').on(
      BODY_EVENTS.customerLogin,
      /**
       * Handle customer login
       * @param {jQuery.Event} event
       * @param {string} customerID The ID of the just-logged customer
       */
      (event, customerID) => {
        const guestCartFromLocalStorage = getCartFromLocalStorage();
        if (guestCartFromLocalStorage) {
          // Associate current cart to the just-logged user
          setCartToCustomerInLocalStorage(
            guestCartFromLocalStorage,
            customerID
          );
          // Remove the guest cart
          removeCartFromLocalStorage();
        }
      }
    );
  },
  getCartFromLocalStorage,
  findProductInCart,
  removeCartFromLocalStorage,
};
